import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Settings.css';

function Settings() {
  const [activeTab, setActiveTab] = useState('general');
  const [autoRefresh, setAutoRefresh] = useState(() => {
    return localStorage.getItem('autoRefresh') === 'true';
  });
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshLog, setRefreshLog] = useState([]);
  const [syncingFulfillment, setSyncingFulfillment] = useState(false);
  const [blendYield, setBlendYield] = useState('0.97');
  const [savingYield, setSavingYield] = useState(false);

  // Operators state
  const [operators, setOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [showAddOperator, setShowAddOperator] = useState(false);
  const [newOperator, setNewOperator] = useState({ first_name: '', last_name: '' });
  const [editingPinId, setEditingPinId] = useState(null);
  const [pinInput, setPinInput] = useState('');

  // Shipping boxes state
  const [boxes, setBoxes] = useState([]);
  const [showAddBox, setShowAddBox] = useState(false);
  const [newBox, setNewBox] = useState({
    name: '',
    length: '',
    width: '',
    height: '',
    max_weight: '',
    icon: ''
  });

  useEffect(() => {
    localStorage.setItem('autoRefresh', autoRefresh);
  }, [autoRefresh]);

  useEffect(() => {
    // Fetch refresh log
    const fetchRefreshLog = async () => {
      try {
        const response = await axios.get('/api/workorders/refresh-log');
        setRefreshLog(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to fetch refresh log:', err);
      }
    };

    fetchRefreshLog();
    const interval = setInterval(fetchRefreshLog, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchBlendYield();
    fetchBoxes();
    fetchOperators();
  }, []);

  const [yieldFetchError, setYieldFetchError] = useState(false);

  const fetchBlendYield = async () => {
    try {
      const response = await axios.get('/api/settings/blend_yield_percentage');
      setBlendYield(response.data.setting_value);
      setYieldFetchError(false);
    } catch (err) {
      console.error('Failed to fetch blend yield:', err);
      setYieldFetchError(true);
    }
  };

  const fetchOperators = async () => {
    setLoadingOperators(true);
    try {
      const response = await axios.get('/api/operators');
      setOperators(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch operators:', err);
      setOperators([]);
    } finally {
      setLoadingOperators(false);
    }
  };

  const handleAddOperator = async () => {
    if (!newOperator.first_name || !newOperator.last_name) {
      alert('Please enter first and last name');
      return;
    }

    try {
      // Generate barcode and display name
      const response = await axios.post('/api/operators', {
        first_name: newOperator.first_name,
        last_name: newOperator.last_name
      });
      alert(`Operator added! Badge code: ${response.data.barcode}`);
      setNewOperator({ first_name: '', last_name: '' });
      setShowAddOperator(false);
      await fetchOperators();
    } catch (err) {
      console.error('Failed to add operator:', err);
      alert('Failed to add operator: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleToggleOperatorActive = async (operatorId, isActive) => {
    try {
      await axios.put(`/api/operators/${operatorId}`, { is_active: !isActive });
      await fetchOperators();
    } catch (err) {
      console.error('Failed to update operator:', err);
      alert('Failed to update operator');
    }
  };

  const handleSavePin = async (operatorId) => {
    if (pinInput && !/^\d{4}$/.test(pinInput)) {
      alert('PIN must be exactly 4 digits');
      return;
    }
    try {
      await axios.put(`/api/operators/${operatorId}`, { pin: pinInput });
      setEditingPinId(null);
      setPinInput('');
      await fetchOperators();
    } catch (err) {
      console.error('Failed to update PIN:', err);
      alert('Failed to update PIN: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteOperator = async (operatorId, operatorName) => {
    if (!window.confirm(`Are you sure you want to delete operator "${operatorName}"?`)) return;
    try {
      await axios.delete(`/api/operators/${operatorId}`);
      await fetchOperators();
    } catch (err) {
      console.error('Failed to delete operator:', err);
      alert('Failed to delete operator');
    }
  };

  const handleManualRefresh = async () => {
    try {
      await axios.post('/api/workorders/refresh');
      setLastUpdate(new Date());
      alert('Cache refreshed successfully!');
    } catch (err) {
      console.error('Failed to refresh:', err);
      alert('Failed to refresh cache');
    }
  };

  const handleFulfillmentSync = async () => {
    setSyncingFulfillment(true);
    try {
      const response = await axios.get('/api/pending-fulfillment?refresh=true');
      alert(`Pending fulfillment synced successfully! ${response.data.length} orders updated.`);
    } catch (err) {
      console.error('Failed to sync pending fulfillment:', err);
      alert('Failed to sync pending fulfillment orders');
    } finally {
      setSyncingFulfillment(false);
    }
  };

  const handleSaveBlendYield = async () => {
    const yieldValue = parseFloat(blendYield);

    if (isNaN(yieldValue) || yieldValue <= 0 || yieldValue > 1) {
      alert('Please enter a valid yield percentage between 0 and 1 (e.g., 0.97 for 97%)');
      return;
    }

    setSavingYield(true);
    try {
      await axios.put('/api/settings/blend_yield_percentage', {
        setting_value: String(yieldValue)
      });
      await fetchBlendYield();
      alert('Blend yield percentage updated successfully!');
    } catch (err) {
      console.error('Failed to update blend yield:', err);
      alert('Failed to update blend yield: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingYield(false);
    }
  };

  // Shipping boxes handlers
  const fetchBoxes = async () => {
    try {
      const response = await axios.get('/api/settings/shipping-boxes');
      setBoxes(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch boxes:', err);
      setBoxes([]);
    }
  };

  const handleAddBox = async () => {
    if (!newBox.name || !newBox.length || !newBox.width || !newBox.height) {
      alert('Please enter box name and dimensions');
      return;
    }

    try {
      await axios.post('/api/settings/shipping-boxes', newBox);
      alert('Box added successfully!');
      setNewBox({ name: '', length: '', width: '', height: '', max_weight: '', icon: '📦' });
      setShowAddBox(false);
      await fetchBoxes();
    } catch (err) {
      console.error('Failed to add box:', err);
      alert('Failed to add box: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteBox = async (boxId, boxName) => {
    if (!window.confirm(`Are you sure you want to delete box "${boxName}"?`)) return;
    try {
      await axios.delete(`/api/settings/shipping-boxes/${boxId}`);
      await fetchBoxes();
    } catch (err) {
      console.error('Failed to delete box:', err);
      alert('Failed to delete box');
    }
  };

  const handleToggleBoxActive = async (boxId, isActive) => {
    try {
      await axios.put(`/api/settings/shipping-boxes/${boxId}`, { is_active: !isActive });
      await fetchBoxes();
    } catch (err) {
      console.error('Failed to update box:', err);
      alert('Failed to update box');
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-container">
        <h1>Settings</h1>

        {/* Tab Navigation */}
        <div className="settings-tabs">
          <button
            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          <button
            className={`tab-button ${activeTab === 'shipping' ? 'active' : ''}`}
            onClick={() => setActiveTab('shipping')}
          >
            Shipping
          </button>
          <button
            className={`tab-button ${activeTab === 'production' ? 'active' : ''}`}
            onClick={() => setActiveTab('production')}
          >
            Production
          </button>
        </div>

        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="tab-content">

        <div className="settings-section">
          <h2>Auto-Refresh</h2>
          <p className="settings-description">
            Enable automatic refresh of work orders every 30 seconds
          </p>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">
              {autoRefresh ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>

        <div className="settings-section">
          <h2>Manual Refresh</h2>
          <p className="settings-description">
            Force refresh the work orders cache from NetSuite
          </p>
          <button className="refresh-btn" onClick={handleManualRefresh}>
            Refresh Cache Now
          </button>
          {lastUpdate && (
            <p className="last-update">
              Last refreshed: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>

        <div className="settings-section">
          <h2>Pending Fulfillment Cache</h2>
          <p className="settings-description">
            Force sync pending fulfillment orders from NetSuite. This cache auto-updates every 10 minutes.
          </p>
          <button
            className="refresh-btn"
            onClick={handleFulfillmentSync}
            disabled={syncingFulfillment}
          >
            {syncingFulfillment ? 'Syncing from NetSuite...' : 'Sync Pending Fulfillment'}
          </button>
          <p className="settings-note">
            ⚡ Cache automatically refreshes every 10 minutes in the background
          </p>
        </div>

        <div className="settings-section">
          <h2>Refresh Log</h2>
          <p className="settings-description">
            Recent cache refresh activity
          </p>
          <div className="refresh-log">
            {refreshLog.length === 0 ? (
              <p className="log-empty">No refresh activity yet</p>
            ) : (
              <table className="log-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Work Orders</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {(refreshLog || []).slice(0, 20).map((entry, index) => (
                    <tr key={index} className={entry.status === 'error' ? 'log-error' : ''}>
                      <td>{new Date(entry.timestamp).toLocaleString()}</td>
                      <td>
                        <span className={`status-badge ${entry.status}`}>
                          {entry.status}
                        </span>
                      </td>
                      <td>{entry.workOrderCount || '-'}</td>
                      <td>{entry.duration ? `${entry.duration}ms` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

          </div>
        )}

        {/* Production Tab */}
        {activeTab === 'production' && (
          <div className="tab-content">
            {/* Operators Section */}
            <div className="settings-section">
              <h2>Production Operators</h2>
              <p className="settings-description">
                Manage operators who work on the production floor. Each operator gets a barcode for quick login at stations.
              </p>

              <div className="user-actions-bar">
                <button
                  className="add-user-btn"
                  onClick={() => setShowAddOperator(!showAddOperator)}
                >
                  {showAddOperator ? 'Cancel' : 'Add Operator'}
                </button>
                <a
                  href="/production/badges"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="add-user-btn invite-btn"
                  style={{ textDecoration: 'none' }}
                >
                  Print Badges
                </a>
                <a
                  href="/production/carting/performance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="add-user-btn"
                  style={{ textDecoration: 'none', background: 'transparent', border: '1px solid #8B6F4E', color: '#8B6F4E' }}
                >
                  View Performance
                </a>
              </div>

              {showAddOperator && (
                <form
                  className="add-user-form"
                  onSubmit={(e) => { e.preventDefault(); handleAddOperator(); }}
                >
                  <input
                    type="text"
                    placeholder="First Name"
                    value={newOperator.first_name}
                    onChange={(e) => setNewOperator({...newOperator, first_name: e.target.value})}
                    autoComplete="given-name"
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={newOperator.last_name}
                    onChange={(e) => setNewOperator({...newOperator, last_name: e.target.value})}
                    autoComplete="family-name"
                  />
                  <button type="submit" className="save-user-btn">
                    Add Operator
                  </button>
                </form>
              )}

              <div className="users-list">
                {loadingOperators ? (
                  <p className="log-empty">Loading operators...</p>
                ) : !operators || operators.length === 0 ? (
                  <p className="log-empty">No operators configured</p>
                ) : (
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Badge Code</th>
                        <th>PIN</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operators.map((op) => (
                        <tr key={op.id} className={!op.is_active ? 'user-inactive' : ''}>
                          <td>{op.first_name} {op.last_name}</td>
                          <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{op.barcode}</td>
                          <td>
                            {editingPinId === op.id ? (
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  maxLength={4}
                                  placeholder="4 digits"
                                  value={pinInput}
                                  onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                  style={{ width: '70px', padding: '4px 8px', fontFamily: 'monospace' }}
                                  autoFocus
                                />
                                <button
                                  className="action-btn"
                                  onClick={() => handleSavePin(op.id)}
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                >
                                  Save
                                </button>
                                <button
                                  className="action-btn"
                                  onClick={() => { setEditingPinId(null); setPinInput(''); }}
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <span style={{ color: op.has_pin ? '#4a7c59' : '#999' }}>
                                  {op.has_pin ? '****' : 'No PIN'}
                                </span>
                                <button
                                  className="action-btn"
                                  onClick={() => { setEditingPinId(op.id); setPinInput(''); }}
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                >
                                  {op.has_pin ? 'Change' : 'Set'}
                                </button>
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${op.is_active ? 'active' : 'inactive'}`}>
                              {op.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="user-actions">
                            <button
                              className="action-btn"
                              onClick={() => handleToggleOperatorActive(op.id, op.is_active)}
                            >
                              {op.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteOperator(op.id, `${op.first_name} ${op.last_name}`)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Blend Yield Section */}
            <div className="settings-section">
              <h2>Blend Yield Percentage</h2>
              <p className="settings-description">
                Set the default yield percentage for blend calculations. This accounts for material loss during production.
                For example, 0.97 means 97% yield (3% loss).
              </p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="1"
                  value={blendYield}
                  onChange={(e) => setBlendYield(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    width: '120px'
                  }}
                />
                <span style={{ fontSize: '14px', color: '#666' }}>
                  ({(parseFloat(blendYield || 0) * 100).toFixed(2)}% yield)
                </span>
                <button
                  className="refresh-btn"
                  onClick={handleSaveBlendYield}
                  disabled={savingYield}
                  style={{ marginLeft: '10px' }}
                >
                  {savingYield ? 'Saving...' : 'Save Yield Setting'}
                </button>
              </div>
              {yieldFetchError && (
                <p style={{ marginTop: '8px', fontSize: '13px', color: '#A07262' }}>
                  Could not load saved yield from database. Showing default value.
                </p>
              )}
              <p className="settings-note" style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
                This setting will be used as the default for all new blends. You can override it per blend if needed.
              </p>
            </div>
          </div>
        )}

        {/* Shipping Tab */}
        {activeTab === 'shipping' && (
          <div className="tab-content">
            <div className="settings-section">
              <h2>Shipping Boxes</h2>
              <p className="settings-description">
                Manage available shipping box types for fulfillment. Dimensions are used for accurate shipping calculations.
              </p>

              <button
                className="add-user-btn"
                onClick={() => setShowAddBox(!showAddBox)}
              >
                {showAddBox ? 'Cancel' : 'Add New Box'}
              </button>

              {showAddBox && (
                <form
                  className="add-user-form"
                  onSubmit={(e) => { e.preventDefault(); handleAddBox(); }}
                >
                  <input
                    type="text"
                    placeholder="Box Name (e.g., Small Box)"
                    value={newBox.name}
                    onChange={(e) => setNewBox({...newBox, name: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Icon (emoji)"
                    value={newBox.icon}
                    onChange={(e) => setNewBox({...newBox, icon: e.target.value})}
                    style={{ maxWidth: '80px' }}
                  />
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Length (inches)"
                    value={newBox.length}
                    onChange={(e) => setNewBox({...newBox, length: e.target.value})}
                  />
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Width (inches)"
                    value={newBox.width}
                    onChange={(e) => setNewBox({...newBox, width: e.target.value})}
                  />
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Height (inches)"
                    value={newBox.height}
                    onChange={(e) => setNewBox({...newBox, height: e.target.value})}
                  />
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Max Weight (lbs, optional)"
                    value={newBox.max_weight}
                    onChange={(e) => setNewBox({...newBox, max_weight: e.target.value})}
                  />
                  <button type="submit" className="save-user-btn">
                    Save Box
                  </button>
                </form>
              )}

              <div className="users-list">
                {!boxes || boxes.length === 0 ? (
                  <p className="log-empty">No shipping boxes configured</p>
                ) : (
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Icon</th>
                        <th>Name</th>
                        <th>Dimensions (L×W×H)</th>
                        <th>Max Weight</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(boxes || []).map((box) => (
                        <tr key={box.id} className={!box.is_active ? 'user-inactive' : ''}>
                          <td style={{ fontSize: '24px' }}>{box.icon || '📦'}</td>
                          <td>{box.name}</td>
                          <td>{box.length}×{box.width}×{box.height} in</td>
                          <td>{box.max_weight ? `${box.max_weight} lbs` : 'N/A'}</td>
                          <td>
                            <span className={`status-badge ${box.is_active ? 'active' : 'inactive'}`}>
                              {box.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="user-actions">
                            <button
                              className="action-btn"
                              onClick={() => handleToggleBoxActive(box.id, box.is_active)}
                            >
                              {box.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteBox(box.id, box.name)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default Settings;
